import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UserService } from '../../services/user.service';
import { ContactService } from '../../services/contact.service';
import { CompanyService } from '../../services/company.service';

import { ContactEmailInformation, SendQuestionInformation } from '../../models/contact';
import { CompanyInformation } from '../../models/company';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent {
  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;
  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;
  showSubmitMessageSpinner = false;
  showSuccess = false;
  showError = false;

  companyInformation: CompanyInformation = null;
  companyInformationFound = false;
  contactEmailInformation: ContactEmailInformation = null;
  contactEmailInformationFound = false;

  emailAddress: string = null;
  emailMessage: string = null;

  contactUsForm: UntypedFormGroup;
  emailAddressFormControl = new UntypedFormControl();
  emailMessageFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  constructor(
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _contactService: ContactService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('ContactUsComponent');
    this.getData();
    this.scrollToTop();
  }

  getData(): void {
    this.showSpinner = true;
    const participantId: number = this._userService.getParticipantId();

    this.companyInformation = this._companyService.getNewCompanyInformation();

    this._companyService.companyInformation.subscribe(
      response => this.onCompanyInformationChanged(response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this.getContactEmailInformation(participantId);
  }

  getContactEmailInformation(participantId: number): void {
    if (participantId !== null) {
      this.showSpinner = true;
      this.contactEmailInformationFound = false;
      this._contactService.getContactEmailInformation(participantId).subscribe(
        result => {
          this.contactEmailInformationFound = result !== null && result.participantId !== null && result.participantId > 0;
          this.contactEmailInformation = result;

          if (this._logging.serviceCallTraceLog) {
            console.log('ContactUsComponent.getContactEmailInformation :: result.homeEmail = ' + result.homeEmail);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ContactUsComponent.getContactEmailInformation :: result = ' + JSON.stringify(result));
          }

          this.emailAddress = this.contactEmailInformation.homeEmail;
          this.emailMessage = null;

          this.createForm();
          this.getDataComplete();
        },
        error => this._handleError('getContactEmailInformation', error)
      );
    } else {
      this.contactEmailInformation = {
        participantId: null,
        firstName: null,
        middleInitial: null,
        lastName: null,
        homeEmail: null,
        skipTraceCode: null
      };
      this.contactEmailInformationFound = true;
      this.emailAddress = null;
      this.emailMessage = null;

      this.createForm();
      this.getDataComplete();
    }
  }

  getDataComplete(): void {
    if (this.companyInformationFound && this.contactEmailInformationFound) {
      this.showSpinner = false;
    }
  }

  onCompanyInformationChanged(newCompanyInformation: CompanyInformation): void {
    this.companyInformation = newCompanyInformation;
    this.companyInformationFound =
      this.companyInformation !== null && this.companyInformation.companyName !== null && this.companyInformation.companyName.length > 0;
  }

  createForm(): void {
    this.contactUsForm = this._formBuilder.group({});
    this.contactUsForm.addControl('emailAddressFormControl', this.emailAddressFormControl);
    this.contactUsForm.addControl('emailMessageFormControl', this.emailMessageFormControl);
    this.contactUsForm.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.contactUsForm.setValue({
      emailAddressFormControl: this.emailAddress,
      emailMessageFormControl: this.emailMessage,
      recaptchaFormControl: null
    });

    this.contactUsForm
      .get('emailAddressFormControl')
      .setValidators([Validators.required, Validators.pattern(this.fieldValidations.contact.emailAddressPattern)]);
    this.contactUsForm.get('emailMessageFormControl').setValidators([Validators.required]);
    this.contactUsForm.get('recaptchaFormControl').setValidators([Validators.required]);
  }

  formatPhoneNumber(phoneNumber: string): string {
    return phoneNumber !== null && phoneNumber.length > 7
      ? phoneNumber.substr(0, 3) + '.' + phoneNumber.substr(3, 3) + '.' + phoneNumber.substr(6)
      : '';
  }

  submitMessage(): void {
    this.showError = false;
    this.showSuccess = false;
    this.showSubmitMessageSpinner = true;

    const sendQuestionInformation: SendQuestionInformation = {
      participantId: this._userService.getParticipantId(),
      emailAddress: this.contactUsForm.get('emailAddressFormControl').value,
      questionText: this.contactUsForm.get('emailMessageFormControl').value,
      companyCode: this._companyService.getCompanyCode()
    };

    this._contactService.sendQuestion(sendQuestionInformation).subscribe(
      result => {
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactUsComponent.submitMessage :: result = ' + result);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactUsComponent.submitMessage :: result = ' + result);
        }

        this.showSubmitMessageSpinner = false;

        if (Number(result) === 0) {
          this.showError = false;
          this.showSuccess = true;
        }  else {
                  this.showError = true;
                  this.showSuccess = false;
                 }
      },
      error => {
        this._handleError('submitMessage', error, sendQuestionInformation);
        this.showError = true;
      }
    );
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
